import React, { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import Dialog from "../../components/ui/Dialog";
import Pagination from "../../components/ui/Pagination";
import {
  useApproveCutoff,
  useCutoffEmployeeDays,
  useCutoffEmployees,
  useReopenCutoff,
  useUnapproveCutoff,
} from "../../api/queries/approvalQueries";
import { useReviewCorrection } from "../../api/queries/workflowQueries";
import { useCanModify } from "../../hooks/usePermissions";
import useCurrentUser from "../../hooks/useCurrentUser";
import { getApiErrorMessage } from "../../utils/apiError";
import {
  formatDateTime,
  formatPlainDate,
  formatTime,
} from "../../utils/dateTime";
import { properName } from "../../utils/text";
import DtrDaysTable from "../../me/features/dtr/components/DtrDaysTable";
import { CutoffSummary } from "../../me/features/dtr/components/DtrTotals";
import LocationsModal from "../attendance/components/LocationsModal";
import { CutoffStatusBadge } from "../dtr/components/DtrBits";
import { totalsText } from "../dtr/components/dtrFormat";

const EMPTY = [];
const EXPAND_ALL_LIMIT = 50;

/** Approve / reject a pending correction from its day (one step: the first decision is final). */
export const CorrectionDecision = ({
  correction,
  employeeName,
  date,
  onDone,
}) => {
  const [rejecting, setRejecting] = useState(false);
  const [remarks, setRemarks] = useState("");
  const review = useReviewCorrection();
  const decide = (action) =>
    review.mutate(
      { id: correction.id, action, remarks: remarks.trim() || undefined },
      {
        onSuccess: () => {
          toast.success(
            action === "APPROVE"
              ? "Correction approved: the day is updated."
              : "Correction rejected.",
          );
          setRejecting(false);
          onDone?.();
        },
        onError: (err) =>
          toast.error(getApiErrorMessage(err, "Could not save the decision.")),
      },
    );
  const req = correction.requested || {};
  return (
    <span className="d-inline-flex flex-wrap align-items-center gap-1 justify-content-end">
      <span className="fs-070 text-slate-500 me-1">
        Asked: {req.checkInTime ? formatTime(req.checkInTime) : "same"} –{" "}
        {req.checkOutTime ? formatTime(req.checkOutTime) : "same"}
      </span>
      <button
        type="button"
        className="btn btn-sm btn-success att-action-btn"
        onClick={() => decide("APPROVE")}
        disabled={review.isPending}
      >
        Approve
      </button>
      <button
        type="button"
        className="btn btn-sm btn-outline-danger att-action-btn"
        onClick={() => setRejecting(true)}
        disabled={review.isPending}
      >
        Reject
      </button>
      {rejecting && (
        <Dialog
          title="Reject correction"
          subtitle={`${employeeName} · ${formatPlainDate(date, "ddd, MMM D")}`}
          onClose={() => setRejecting(false)}
          busy={review.isPending}
          footer={
            <>
              <button
                type="button"
                className="btn btn-outline-secondary profile-btn"
                onClick={() => setRejecting(false)}
                disabled={review.isPending}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-danger profile-btn"
                onClick={() => decide("REJECT")}
                disabled={review.isPending || !remarks.trim()}
              >
                Reject
              </button>
            </>
          }
        >
          <label htmlFor="corr-reject" className="profile-label">
            Reason <span className="text-danger">*</span>
          </label>
          <textarea
            id="corr-reject"
            className="form-control"
            rows={2}
            maxLength={500}
            value={remarks}
            onChange={(e) => setRemarks(e.target.value)}
            autoFocus
          />
        </Dialog>
      )}
    </span>
  );
};

/** One employee's days: from the list (Expand all) or loaded when the row opens. */
const EmployeeDays = ({ row, period, companyId, canDecide, onLocations }) => {
  const loaded = row.days;
  const q = useCutoffEmployeeDays(
    row.employee.id,
    { ...period, companyId },
    { enabled: !loaded },
  );
  const days = loaded || q.data?.days;
  if (!loaded && q.isLoading)
    return (
      <div className="text-center py-3">
        <span
          className="spinner-border spinner-border-sm text-primary"
          role="status"
          aria-label="Loading"
        ></span>
      </div>
    );
  if (!loaded && q.isError)
    return (
      <div className="text-danger fs-080 p-2">
        {getApiErrorMessage(q.error, "Could not load the days.")}
      </div>
    );
  const name = properName(row.employee.name);
  return (
    <DtrDaysTable
      days={days || EMPTY}
      onViewLocations={onLocations}
      renderAction={
        canDecide
          ? (day) =>
              day.pendingCorrection && (
                <CorrectionDecision
                  correction={day.pendingCorrection}
                  employeeName={name}
                  date={day.date}
                />
              )
          : undefined
      }
    />
  );
};

/** Results of a bulk approve / unapprove: a toast, plus the failures listed. */
const reportResults = (res, verb, setFailures) => {
  const failed = (res?.results || EMPTY).filter((r) => !r.ok);
  const done = res?.approved ?? res?.unapproved ?? res?.reopened ?? 0;
  if (done) toast.success(`${done} employee(s) ${verb}.`);
  setFailures(failed);
  if (!done && failed.length) toast.error(`Nothing ${verb}: see the reasons.`);
};

/** "Reopened by X on <date>: <reason>" while a reopened cut-off is not exported again. */
export const ReopenedNote = ({ reopened, status }) =>
  reopened && status !== "EXPORTED" ? (
    <div className="fs-070 text-warning-emphasis mt-1">
      <i className="bi bi-unlock me-1"></i>
      Reopened by {reopened.byName || "HR"} on {formatDateTime(reopened.at)}
      {reopened.reason ? `: ${reopened.reason}` : ""}
    </div>
  ) : null;

/** Company-wide HR: reopen processed (exported) cut-offs to fix them; a reason is required. */
const ReopenDialog = ({ names, busy, onCancel, onConfirm }) => {
  const [reason, setReason] = useState("");
  const text = reason.trim();
  const ok = text.length >= 5 && text.length <= 500;
  return (
    <Dialog
      title="Reopen the processed cut-off?"
      subtitle={names.length === 1 ? names[0] : `${names.length} employees`}
      onClose={onCancel}
      busy={busy}
      footer={
        <>
          <button
            type="button"
            className="btn btn-outline-secondary profile-btn"
            onClick={onCancel}
            disabled={busy}
          >
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-warning profile-btn"
            onClick={() => onConfirm(text)}
            disabled={busy || !ok}
          >
            {busy && (
              <span
                className="spinner-border spinner-border-sm"
                aria-hidden="true"
              ></span>
            )}
            Reopen
          </button>
        </>
      }
    >
      <p className="fs-085">
        The cut-off becomes open and editable again. After fixing it, approve it
        and export again: that export is a correction for payroll.
      </p>
      <label htmlFor="reopen-reason" className="profile-label">
        Reason <span className="text-danger">*</span>
      </label>
      <textarea
        id="reopen-reason"
        className="form-control"
        rows={3}
        maxLength={500}
        value={reason}
        onChange={(e) => setReason(e.target.value)}
        placeholder="What needs fixing (at least 5 characters)"
        autoFocus
      />
      <div className="fs-070 text-slate-500 mt-1">
        Saved with your name in the audit log.
      </div>
    </Dialog>
  );
};

/**
 * Attendance & DTR → DTR Entries: My DTR for every employee under the user.
 * Employees of a cut-off (only those with time entries by
 * default), status, totals and pending requests; expand a row (or all) for
 * the same day list as My DTR; decide corrections on the day; HR approves
 * early (optional: exporting approves) and exports from the Export tab.
 */
const DtrTab = ({ period, shared }) => {
  const user = useCurrentUser();
  const canModify = useCanModify("dtr");
  const canDecide = useCanModify("dtr"); // corrections are reviewed by DTR approvers
  // Reopening a processed cut-off: company-wide DTR modify only
  const canReopen = useCanModify("dtr", { company: true });
  const [reopenIds, setReopenIds] = useState(null); // employee ids in the dialog
  const [entries, setEntries] = useState("true");
  const [status, setStatus] = useState("");
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20);
  const [expandAll, setExpandAll] = useState(false);
  const [open, setOpen] = useState(() => new Set());
  const [selected, setSelected] = useState(() => new Set());
  const [failures, setFailures] = useState(EMPTY);
  const [locationsFor, setLocationsFor] = useState(null);
  const approve = useApproveCutoff();
  const unapprove = useUnapproveCutoff();
  const reopen = useReopenCutoff();
  const busy = approve.isPending || unapprove.isPending || reopen.isPending;
  const companyId = shared.companyId || undefined;
  // Plain values for the hooks below (shared / period are new objects each render)
  const branchId = shared.branchId || undefined;
  const assignmentType = shared.assignmentType || undefined;
  const search = shared.search || undefined;
  const pKey = period ? `${period.year}-${period.month}-${period.cutoff}` : "";

  const filters = useMemo(
    () =>
      period
        ? {
            ...period,
            companyId,
            branchId,
            assignmentType,
            search,
            withEntries: entries,
            status: status || undefined,
            page,
            limit: expandAll ? Math.min(limit, EXPAND_ALL_LIMIT) : limit,
            includeDays: expandAll || undefined,
          }
        : null,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      pKey,
      companyId,
      branchId,
      assignmentType,
      search,
      entries,
      status,
      page,
      limit,
      expandAll,
    ],
  );
  const q = useCutoffEmployees(filters, { enabled: !!period });
  const rows = q.data?.data || EMPTY;
  const summary = q.data?.summary || {};
  const canApprove = !!q.data?.canApprove;
  // Expand all: the list brings every row's days; open them all once loaded
  useEffect(() => {
    if (expandAll && q.data?.data)
      setOpen(new Set(q.data.data.map((r) => r.employee.id)));
  }, [expandAll, q.data]);
  const collapseAll = () => {
    setExpandAll(false);
    setOpen(new Set());
  };

  // New list → nothing selected, nothing failed
  useEffect(() => {
    setSelected(new Set());
    setFailures(EMPTY);
  }, [filters]);
  useEffect(
    () => setPage(1),
    [pKey, branchId, assignmentType, search, entries, status],
  );

  const toggleOpen = (id) =>
    setOpen((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  const toggleSelect = (id) =>
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  const selectable = (r) =>
    canModify && r.status !== "EXPORTED" && r.employee.id !== user.employeeId;
  const pageSelectable = rows.filter(selectable);
  const allSelected =
    pageSelectable.length > 0 &&
    pageSelectable.every((r) => selected.has(r.employee.id));

  const run = (kind, employeeIds) => {
    const body = { ...period, companyId, employeeIds };
    const mutation = kind === "approve" ? approve : unapprove;
    mutation.mutate(body, {
      onSuccess: (res) => {
        reportResults(
          res,
          kind === "approve" ? "approved" : "unapproved",
          setFailures,
        );
        setSelected(new Set());
      },
      onError: (err) =>
        toast.error(getApiErrorMessage(err, `Could not ${kind} the cut-off.`)),
    });
  };
  const doReopen = (reason) =>
    reopen.mutate(
      { ...period, companyId, employeeIds: reopenIds, reason },
      {
        onSuccess: (res) => {
          reportResults(res, "reopened", setFailures);
          setReopenIds(null);
        },
        onError: (err) =>
          toast.error(getApiErrorMessage(err, "Could not reopen the cut-off.")),
      },
    );
  const nameOf = (id) =>
    properName(rows.find((r) => r.employee.id === id)?.employee.name || id);

  if (!period)
    return (
      <div className="text-center py-5">
        <span
          className="spinner-border text-primary"
          role="status"
          aria-label="Loading"
        ></span>
      </div>
    );

  return (
    <>
      <div className="d-flex flex-wrap align-items-center gap-2 mb-3">
        <select
          className="status-select"
          value={entries}
          onChange={(e) => setEntries(e.target.value)}
          aria-label="Time entries"
        >
          <option value="true">With time entries</option>
          <option value="false">No time entries</option>
          <option value="all">Everyone</option>
        </select>
        <select
          className="status-select"
          value={status}
          onChange={(e) => setStatus(e.target.value)}
          aria-label="Cut-off status"
        >
          <option value="">All statuses</option>
          <option value="OPEN">Open</option>
          <option value="APPROVED">Approved</option>
          <option value="EXPORTED">Processed</option>
        </select>
        <div className="btn-group btn-group-sm" role="group" aria-label="Rows">
          <button
            type="button"
            className="btn btn-light border"
            onClick={() => setExpandAll(true)}
            disabled={!rows.length}
            title={`Opens every employee on this page (up to ${EXPAND_ALL_LIMIT})`}
          >
            <i className="bi bi-arrows-expand me-1"></i>Expand all
          </button>
          <button
            type="button"
            className="btn btn-light border"
            onClick={collapseAll}
            disabled={!open.size}
          >
            <i className="bi bi-arrows-collapse me-1"></i>Collapse all
          </button>
        </div>
      </div>

      {/* Cut-off header, like My DTR: the period, its numbers, status counts */}
      <div className="att-card mb-3 d-flex flex-wrap align-items-center gap-3">
        <div>
          <div className="profile-label mb-0">Cut-off</div>
          <div className="fw-semibold">{q.data?.period?.label || "—"}</div>
          {q.data?.processOn && (
            <div className="fs-075 text-slate-500">
              Process by {formatPlainDate(q.data.processOn)}
            </div>
          )}
        </div>
        {summary.totals && (
          <div className="flex-grow-1 min-w-0">
            <CutoffSummary totals={summary.totals} title="Cut-off total" />
          </div>
        )}
        <div className="d-flex flex-wrap gap-2">
          {[
            ["employees", "Employees", "gray"],
            ["open", "Open", "gray"],
            ["approved", "Approved", "green"],
            ["exported", "Processed", "blue"],
            ["changedAfterApproval", "Changed after approval", "amber"],
          ].map(([key, label, tone]) => (
            <span key={key} className={`status-pill status-pill--${tone}`}>
              {label}: {summary[key] ?? 0}
            </span>
          ))}
        </div>
      </div>

      {canModify && selected.size > 0 && (
        <div className="att-bulk-bar" role="region" aria-label="Bulk actions">
          <strong className="fs-085">{selected.size} selected</strong>
          <div className="d-flex flex-wrap gap-2">
            <button
              type="button"
              className="btn btn-sm btn-outline-secondary bg-white"
              onClick={() => run("unapprove", [...selected])}
              disabled={busy}
            >
              Unapprove
            </button>
            <button
              type="button"
              className="btn btn-sm btn-link text-slate-600"
              onClick={() => setSelected(new Set())}
            >
              Clear
            </button>
          </div>
        </div>
      )}
      {canModify && (
        <p className="fs-075 text-slate-500 mb-2">
          <i className="bi bi-info-circle me-1"></i>Exporting approves everyone
          it sends to payroll; approving here first is optional.
        </p>
      )}
      {failures.length > 0 && (
        <div className="alert alert-warning fs-080 py-2">
          <strong>Not done:</strong>
          <ul className="mb-0 ps-3">
            {failures.map((f) => (
              <li key={f.employeeId}>
                {nameOf(f.employeeId)}: {f.error}
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="glass-table-card">
        {q.isError ? (
          <div className="approval-empty text-danger">
            <i className="bi bi-exclamation-triangle"></i>
            {getApiErrorMessage(q.error, "Could not load the cut-off.")}
          </div>
        ) : (
          <>
            <div className="table-responsive">
              <table className="table mb-0 att-table">
                <thead className="custom-table-header">
                  <tr>
                    <th className="ps-4 py-3 att-col-expand">
                      {canModify && (
                        <input
                          type="checkbox"
                          className="form-check-input"
                          checked={allSelected}
                          onChange={() =>
                            setSelected(
                              allSelected
                                ? new Set()
                                : new Set(
                                    pageSelectable.map((r) => r.employee.id),
                                  ),
                            )
                          }
                          aria-label="Select all on this page"
                        />
                      )}
                    </th>
                    <th className="py-3">Employee</th>
                    <th className="py-3">Cut-off</th>
                    <th className="py-3">Status</th>
                    <th className="py-3 pe-4 text-end">Actions</th>
                  </tr>
                </thead>
                <tbody className="border-top-0">
                  {rows.map((r) => {
                    const id = r.employee.id;
                    const isOpen = open.has(id);
                    return (
                      <React.Fragment key={id}>
                        <tr
                          className={selected.has(id) ? "att-row-selected" : ""}
                        >
                          <td className="ps-4">
                            {selectable(r) && (
                              <input
                                type="checkbox"
                                className="form-check-input"
                                checked={selected.has(id)}
                                onChange={() => toggleSelect(id)}
                                aria-label={`Select ${r.employee.name}`}
                              />
                            )}
                          </td>
                          <td>
                            <button
                              type="button"
                              className="btn btn-link p-0 text-start text-decoration-none"
                              onClick={() => toggleOpen(id)}
                              aria-expanded={isOpen}
                            >
                              <i
                                className={`bi ${isOpen ? "bi-chevron-down" : "bi-chevron-right"} me-1 text-slate-500`}
                              ></i>
                              <span className="emp-name-text">
                                {properName(r.employee.name)}
                              </span>
                            </button>
                            <div className="emp-sub-text">
                              {[
                                r.employee.employeeNumber,
                                r.employee.branchName,
                                r.employee.assignmentType,
                              ]
                                .filter(Boolean)
                                .join(" · ")}
                            </div>
                          </td>
                          <td className="fs-080">
                            {totalsText(r.totals)}
                            <div className="d-flex flex-wrap gap-1 mt-1">
                              {r.pendingCorrections > 0 && (
                                <span className="status-pill status-pill--amber">
                                  {r.pendingCorrections} correction(s) pending
                                </span>
                              )}
                              {r.pendingOvertime > 0 && (
                                <span className="status-pill status-pill--amber">
                                  {r.pendingOvertime} overtime pending
                                </span>
                              )}
                            </div>
                          </td>
                          <td>
                            <CutoffStatusBadge status={r.status} />
                            {r.changedAfterApproval && (
                              <div
                                className="fs-070 text-warning-emphasis mt-1"
                                title={
                                  r.approvedAt
                                    ? `Approved by ${r.approvedByName || "HR"} on ${formatDateTime(r.approvedAt)}`
                                    : undefined
                                }
                              >
                                <i className="bi bi-exclamation-triangle me-1"></i>
                                Changed after approval
                              </div>
                            )}
                            <ReopenedNote
                              reopened={r.reopened}
                              status={r.status}
                            />
                            {r.status === "EXPORTED" && r.exportedAt && (
                              <div className="fs-070 text-slate-400">
                                Processed {formatDateTime(r.exportedAt)}
                              </div>
                            )}
                            {r.status === "APPROVED" && r.approvedAt && (
                              <div className="fs-070 text-slate-400">
                                {r.approvedByName || "HR"} ·{" "}
                                {formatDateTime(r.approvedAt)}
                              </div>
                            )}
                          </td>
                          <td className="pe-4 text-end text-nowrap">
                            {canModify &&
                              r.status === "OPEN" &&
                              selectable(r) && (
                                <button
                                  type="button"
                                  className="btn btn-sm btn-link att-action-btn"
                                  onClick={() => run("approve", [id])}
                                  disabled={busy || !canApprove}
                                  title={
                                    canApprove
                                      ? "Optional: the export approves anyway"
                                      : "Can be approved after the cut-off ends"
                                  }
                                >
                                  Approve early
                                </button>
                              )}
                            {canModify && r.status === "APPROVED" && (
                              <button
                                type="button"
                                className="btn btn-sm btn-light border att-action-btn"
                                onClick={() => run("unapprove", [id])}
                                disabled={busy}
                              >
                                Unapprove
                              </button>
                            )}
                            {canReopen &&
                              r.status === "EXPORTED" &&
                              r.employee.id !== user.employeeId && (
                                <button
                                  type="button"
                                  className="btn btn-sm btn-light border att-action-btn"
                                  onClick={() => setReopenIds([id])}
                                  disabled={busy}
                                  title="Reopen to fix a mistake; the next export is a correction"
                                >
                                  <i className="bi bi-unlock"></i> Reopen
                                </button>
                              )}
                          </td>
                        </tr>
                        {isOpen && (
                          <tr>
                            <td colSpan={5} className="bg-light p-2">
                              <EmployeeDays
                                row={r}
                                period={period}
                                companyId={companyId}
                                canDecide={canDecide}
                                onLocations={setLocationsFor}
                              />
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                  {!q.isLoading && rows.length === 0 && (
                    <tr>
                      <td colSpan={5} className="approval-empty">
                        <i className="bi bi-calendar2-x"></i>
                        {entries === "false"
                          ? "Everyone has time entries in this cut-off."
                          : "No employees match these filters."}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            {q.isLoading && (
              <div className="text-center py-5">
                <span
                  className="spinner-border text-primary"
                  role="status"
                  aria-label="Loading"
                ></span>
              </div>
            )}
            <Pagination
              page={page}
              limit={expandAll ? Math.min(limit, EXPAND_ALL_LIMIT) : limit}
              total={q.data?.total || 0}
              onPageChange={setPage}
              onLimitChange={(l) => {
                setLimit(l);
                setPage(1);
              }}
            />
          </>
        )}
      </div>

      {reopenIds && (
        <ReopenDialog
          names={reopenIds.map(nameOf)}
          busy={reopen.isPending}
          onCancel={() => setReopenIds(null)}
          onConfirm={doReopen}
        />
      )}
      {locationsFor && (
        <LocationsModal
          attendanceId={locationsFor}
          onClose={() => setLocationsFor(null)}
        />
      )}
    </>
  );
};

export default DtrTab;
